import { useRef, useState } from 'react'
import { Paperclip, Plus, Trash2 } from 'lucide-react'
import {
  Button,
  Drawer,
  Input,
  Select,
  Textarea,
  toast,
} from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker'
import {
  fetchContactDetail,
  useCreateSampleRequest,
  useSampleProducts,
  useSampleReps,
  useUploadSampleLabel,
  type Party,
} from '../api/queries'
import type { CreateLineInput } from '../api/types'
import { CustomerContactPicker } from './CustomerContactPicker'

interface DraftLine extends CreateLineInput {
  _key: number
  isPrototype?: boolean
}

const PROTOTYPE_SENTINEL = 'PROTOTYPE'

export interface NewSampleRequestDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (requestId: string) => void
}

let _lineKeySeq = 1

function emptyLine(): DraftLine {
  return { _key: _lineKeySeq++, product_id: undefined, quantity: 1, uom: 'ea' }
}

export function NewSampleRequestDrawer({
  open,
  onOpenChange,
  onCreated,
}: NewSampleRequestDrawerProps) {
  const create = useCreateSampleRequest()
  const uploadLabel = useUploadSampleLabel()
  const productsQuery = useSampleProducts()
  const products = productsQuery.data?.products ?? []
  const repsQuery = useSampleReps()
  const reps = repsQuery.data?.reps ?? []

  const [customer, setCustomer] = useState<PickerValue>(null)
  const [prospectName, setProspectName] = useState('')
  const [prospectCompany, setProspectCompany] = useState('')
  const [prospectEmail, setProspectEmail] = useState('')
  const [prospectPhone, setProspectPhone] = useState('')
  const [contact, setContact] = useState<PickerValue>(null)
  const [assignedTo, setAssignedTo] = useState('')
  const [shipTo, setShipTo] = useState('')
  const [needBy, setNeedBy] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()])
  const [labelFiles, setLabelFiles] = useState<File[]>([])
  const [uploadingLabels, setUploadingLabels] = useState(false)
  const labelInputRef = useRef<HTMLInputElement>(null)

  function reset() {
    setCustomer(null)
    setProspectName('')
    setProspectCompany('')
    setProspectEmail('')
    setProspectPhone('')
    setContact(null)
    setAssignedTo('')
    setShipTo('')
    setNeedBy('')
    setNotes('')
    setLines([emptyLine()])
    setLabelFiles([])
    setUploadingLabels(false)
  }

  // Unified pick: a company sets just the customer; a person sets the contact
  // AND auto-fills their company + prefills ship-to from their Odoo address
  // (only when empty, so we never clobber a typed address).
  async function handlePartyPick(party: Party) {
    setProspectName('')
    setProspectCompany('')
    setProspectEmail('')
    setProspectPhone('')
    if (party.kind === 'company') {
      setCustomer([party.id, party.name])
      setContact(null)
      return
    }
    // contact
    setContact([party.id, party.name])
    if (party.company_id && party.company_name) {
      setCustomer([party.company_id, party.company_name])
    }
    try {
      const detail = await fetchContactDetail(party.id)
      if (detail.address) setShipTo((cur) => (cur.trim() ? cur : detail.address))
    } catch {
      /* best-effort — ignore lookup failures */
    }
  }

  // The label shown in the unified picker: contact (+ its company) or company.
  const partyLabel = contact
    ? customer
      ? `${contact[1]} · ${customer[1]}`
      : contact[1]
    : customer
      ? customer[1]
      : null

  const hasCustomer = !!customer || prospectName.trim().length > 0
  const isProspectPath = !customer && prospectName.trim().length > 0
  const prospectContactValid =
    !isProspectPath || (prospectCompany.trim().length > 0 && prospectEmail.trim().length > 0)

  function submit() {
    if (!hasCustomer) {
      toast.danger('Pick a customer or enter a prospect name.')
      return
    }
    if (!prospectContactValid) {
      toast.danger('Prospect requests need a company name and email address.')
      return
    }
    const cleanLines: CreateLineInput[] = lines
      .filter((l) => !!l.product_id || (l.isPrototype && !!l.description?.trim()))
      .map((l) => ({
        product_id: l.isPrototype ? undefined : l.product_id,
        product_code: l.isPrototype ? undefined : l.product_code || undefined,
        description: l.isPrototype ? l.description?.trim() : l.description || undefined,
        quantity: l.quantity,
        uom: l.uom || 'ea',
      }))

    create.mutate(
      {
        customer_id: customer ? customer[0] : undefined,
        customer_name: customer ? customer[1] : undefined,
        prospect_name: prospectName.trim() || undefined,
        prospect_company: prospectCompany.trim() || undefined,
        prospect_email: prospectEmail.trim() || undefined,
        prospect_phone: prospectPhone.trim() || undefined,
        contact_id: contact ? contact[0] : undefined,
        contact_name: contact ? contact[1] : undefined,
        assigned_to: assignedTo || undefined,
        ship_to_address: shipTo.trim() || undefined,
        need_by_date: needBy || undefined,
        notes: notes.trim() || undefined,
        lines: cleanLines,
      },
      {
        onSuccess: async (req) => {
          // Request exists now — upload any staged shipping labels against it.
          // A per-file failure never rolls back the request; the operator can
          // re-attach from the card.
          let failures = 0
          if (labelFiles.length > 0) {
            setUploadingLabels(true)
            for (const file of labelFiles) {
              try {
                await uploadLabel.mutateAsync({ requestId: req.id, file })
              } catch {
                failures += 1
              }
            }
            setUploadingLabels(false)
          }
          if (failures > 0) {
            toast.danger(
              `Created ${req.request_number}, but ${failures} label${failures > 1 ? 's' : ''} failed to upload — add ${failures > 1 ? 'them' : 'it'} from the card.`,
            )
          } else {
            toast.success(`Created ${req.request_number}`)
          }
          reset()
          onOpenChange(false)
          onCreated?.(req.id)
        },
        onError: (err) =>
          toast.danger(
            `Create failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  function addLabelFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setLabelFiles((cur) => [...cur, ...Array.from(files)])
    if (labelInputRef.current) labelInputRef.current.value = ''
  }

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
      size="wide"
      title="New Uviva Sample Request"
      meta="Request samples for a customer or prospect — the team is notified automatically."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending || uploadingLabels}
            disabled={!hasCustomer || !prospectContactValid}
            onClick={submit}
          >
            Create request
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Customer or contact</span>
            <CustomerContactPicker
              label={partyLabel}
              onPick={handlePartyPick}
              onClear={() => {
                setCustomer(null)
                setContact(null)
              }}
              placeholder="Search a company or a person…"
            />
            <span className="text-role-metadata">
              Search a company or a person (purchasing, R&D, warehouse…). Picking
              a person fills in their company automatically.
            </span>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">…or prospect name</span>
            <Input
              value={prospectName}
              onChange={(e) => {
                setProspectName(e.target.value)
                if (e.target.value.trim()) {
                  setCustomer(null)
                  setContact(null)
                }
              }}
              placeholder="New / unregistered prospect"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              Company name{isProspectPath ? <span className="text-danger-text"> *</span> : null}
            </span>
            <Input
              value={prospectCompany}
              onChange={(e) => setProspectCompany(e.target.value)}
              placeholder="Prospect's company"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              Email address{isProspectPath ? <span className="text-danger-text"> *</span> : null}
            </span>
            <Input
              type="email"
              value={prospectEmail}
              onChange={(e) => setProspectEmail(e.target.value)}
              placeholder="prospect@company.com"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Phone number</span>
            <Input
              type="tel"
              value={prospectPhone}
              onChange={(e) => setProspectPhone(e.target.value)}
              placeholder="(555) 555-5555"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Need by</span>
          <Input type="date" value={needBy} onChange={(e) => setNeedBy(e.target.value)} />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Assigned rep</span>
          <Select
            value={assignedTo}
            disabled={repsQuery.isLoading}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value="">
              {repsQuery.isLoading ? 'Loading…' : 'Unassigned'}
            </option>
            {reps.map((rep) => (
              <option key={rep.email} value={rep.email}>
                {rep.name}
              </option>
            ))}
          </Select>
          <span className="text-role-metadata">
            Gets shipment + delivery notifications for this request.
          </span>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Ship-to address</span>
          <Textarea
            value={shipTo}
            onChange={(e) => setShipTo(e.target.value)}
            rows={2}
            placeholder="Where should the sample ship?"
          />
        </label>

        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-role-section-label">Requested products</span>
            <Button variant="secondary" size="sm" onClick={() => setLines((l) => [...l, emptyLine()])}>
              <Plus size={14} className="mr-1.5" /> Add item
            </Button>
          </div>
          <div className="flex flex-col gap-2">
            {lines.map((line, idx) => (
              <div key={line._key} className="flex items-end gap-2">
                <label className="flex flex-1 flex-col gap-1 text-sm">
                  {idx === 0 ? <span className="text-role-metadata">Product</span> : null}
                  <Select
                    value={
                      line.isPrototype
                        ? PROTOTYPE_SENTINEL
                        : line.product_id
                          ? String(line.product_id)
                          : ''
                    }
                    disabled={productsQuery.isLoading}
                    onChange={(e) => {
                      const val = e.target.value
                      if (val === PROTOTYPE_SENTINEL) {
                        setLines((ls) =>
                          ls.map((l) =>
                            l._key === line._key
                              ? {
                                  ...l,
                                  isPrototype: true,
                                  product_id: undefined,
                                  product_code: undefined,
                                  description: '',
                                  uom: 'ea',
                                }
                              : l,
                          ),
                        )
                        return
                      }
                      const pid = val ? Number(val) : undefined
                      const prod = products.find((p) => p.product_id === pid)
                      setLines((ls) =>
                        ls.map((l) =>
                          l._key === line._key
                            ? {
                                ...l,
                                isPrototype: false,
                                product_id: pid,
                                product_code: prod?.code ?? undefined,
                                description: prod?.name ?? undefined,
                                uom: prod?.uom ?? 'ea',
                              }
                            : l,
                        ),
                      )
                    }}
                  >
                    <option value="">
                      {productsQuery.isLoading ? 'Loading…' : 'Select a Uviva sample…'}
                    </option>
                    {products.map((p) => (
                      <option key={p.product_id} value={String(p.product_id)}>
                        {p.name}
                        {p.code ? ` (${p.code})` : ''}
                      </option>
                    ))}
                    <option value={PROTOTYPE_SENTINEL}>Prototypes…</option>
                  </Select>
                </label>
                {line.isPrototype ? (
                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    {idx === 0 ? (
                      <span className="text-role-metadata">Prototype name</span>
                    ) : null}
                    <Input
                      value={line.description ?? ''}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l) =>
                            l._key === line._key ? { ...l, description: e.target.value } : l,
                          ),
                        )
                      }
                      placeholder="Describe the prototype being requested"
                    />
                  </label>
                ) : null}
                <label className="flex w-20 flex-col gap-1 text-sm">
                  {idx === 0 ? <span className="text-role-metadata">Qty</span> : null}
                  <Input
                    type="number"
                    min="0"
                    value={line.quantity ?? ''}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((l) =>
                          l._key === line._key
                            ? { ...l, quantity: e.target.value === '' ? undefined : Number(e.target.value) }
                            : l,
                        ),
                      )
                    }
                  />
                </label>
                <label className="flex w-16 flex-col gap-1 text-sm">
                  {idx === 0 ? <span className="text-role-metadata">UOM</span> : null}
                  <Input value={line.uom ?? 'ea'} readOnly tabIndex={-1} />
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Remove item"
                  disabled={lines.length === 1}
                  onClick={() => setLines((ls) => ls.filter((l) => l._key !== line._key))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-role-section-label">Shipping labels</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => labelInputRef.current?.click()}
            >
              <Paperclip size={14} className="mr-1.5" /> Attach label
            </Button>
          </div>
          <input
            ref={labelInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(e) => addLabelFiles(e.target.files)}
          />
          {labelFiles.length === 0 ? (
            <span className="text-role-metadata">
              Optional — attach a prepaid carrier label (PDF or image). You can
              also add labels later from the request card.
            </span>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {labelFiles.map((file, idx) => (
                <li
                  key={`${file.name}-${idx}`}
                  className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-sunk px-3 py-1.5 text-sm"
                >
                  <Paperclip size={14} className="text-secondary" />
                  <span className="flex-1 truncate">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      setLabelFiles((cur) => cur.filter((_, i) => i !== idx))
                    }
                  >
                    <Trash2 size={14} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Notes</span>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything the sample team should know"
          />
        </label>
      </div>
    </Drawer>
  )
}
